import gsap from 'gsap';

type Go = () => void;

let el: HTMLDivElement | null = null;
let active = false;
let tl: gsap.core.Timeline | null = null;
let safety = 0;

export function registerCurtain(node: HTMLDivElement | null) {
  el = node;
}

export function isCurtainActive() {
  return active;
}

function reset() {
  tl?.kill();
  tl = null;
  window.clearTimeout(safety);
  if (el) {
    gsap.set(el, { clipPath: 'inset(0% 100% 0% 0%)', visibility: 'hidden', pointerEvents: 'none' });
  }
  active = false;
}

export function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Editor preview only (inside an iframe): the editor intercepts link clicks and
 * routes through window.REACT_APP_NAVIGATE. Wrap that entry so it uses the same curtain.
 * Published site (top-level window) is untouched.
 */
let installed = false;
export function installPreviewNavigateCurtain() {
  if (installed || window.self === window.top) return;
  installed = true;
  type Nav = (to: unknown, opts?: unknown) => void;
  let raw: Nav | undefined = (window as unknown as { REACT_APP_NAVIGATE?: Nav }).REACT_APP_NAVIGATE;
  const wrapped: Nav = (to, opts) => {
    if (!raw) return;
    const nav = raw;
    const base = __BASE_PATH__.replace(/\/$/, '');
    const current = window.location.pathname.replace(base, '') || '/';
    if (typeof to !== 'string' || to.startsWith('#') || to.split('#')[0] === current || active) {
      if (!active) nav(to, opts);
      return;
    }
    if (!runCurtain(() => nav(to, opts))) nav(to, opts);
  };
  try {
    Object.defineProperty(window, 'REACT_APP_NAVIGATE', {
      configurable: true,
      get: () => (raw ? wrapped : undefined),
      set: (v: Nav) => {
        raw = v;
      },
    });
  } catch {
    // If the property cannot be redefined, fall back to normal navigation.
  }
}

/**
 * Capture-phase click takeover on window: runs before any host (share viewer, editor)
 * document-level click interception, so internal links always get the full
 * cover → navigate → reveal sequence.
 */
let routerNavigate: ((to: string) => void) | null = null;
let clickInstalled = false;
export function installClickCurtain(nav: (to: string) => void) {
  routerNavigate = nav;
  if (clickInstalled) return;
  clickInstalled = true;
  window.addEventListener('click', (e) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const a = (e.target as Element | null)?.closest?.('a[data-curtain-to]') as HTMLAnchorElement | null;
    if (!a) return;
    const t = a.getAttribute('target');
    if (t && t !== '_self') return;
    const to = a.getAttribute('data-curtain-to') || '';
    const base = __BASE_PATH__.replace(/\/$/, '');
    const current = window.location.pathname.replace(base, '') || '/';
    if (!to || to.startsWith('#') || to.split('#')[0] === current) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    e.stopPropagation();
    const nav = routerNavigate;
    if (!nav) return;
    if (!runCurtain(() => nav(to))) nav(to);
  }, true);
}

/** Cover → navigate once → reveal. Returns false if the caller should navigate normally. */
export function runCurtain(go: Go): boolean {
  if (!el || prefersReducedMotion()) return false;
  if (active) return true; // swallow repeated clicks
  active = true;
  let navigated = false;
  const doGo = () => {
    if (navigated) return;
    navigated = true;
    go();
  };
  // Hard fallback: whatever happens, finish navigation and release the UI.
  safety = window.setTimeout(() => {
    doGo();
    reset();
  }, 1600);

  gsap.set(el, { visibility: 'visible', pointerEvents: 'auto', clipPath: 'inset(0% 100% 0% 0%)' });
  tl = gsap.timeline();
  tl.to(el, { clipPath: 'inset(0% 0% 0% 0%)', duration: 0.28, ease: 'power2.inOut' })
    .add(() => {
      doGo();
      tl?.pause();
      // Two frames: new route committed, Header layout effect has set scroll, first paint done.
      requestAnimationFrame(() => requestAnimationFrame(() => tl?.resume()));
    })
    .to(el, { clipPath: 'inset(0% 0% 0% 100%)', duration: 0.34, ease: 'power3.out' })
    .add(reset);
  return true;
}